import React from 'react'
import { computeGst } from '../../services/paymentService'

interface PriceBreakdownProps {
  basePrice: number
  discountPercent: number
  billingState: string
  billingGstin?: string
  accentColor?: 'emerald' | 'orange'
}

function fmt(n: number) {
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`
}

export const PriceBreakdown: React.FC<PriceBreakdownProps> = ({
  basePrice,
  discountPercent,
  billingState,
  billingGstin,
  accentColor = 'orange',
}) => {
  const gst = computeGst(basePrice, discountPercent, billingState, billingGstin)
  const isTN = billingState.toLowerCase() === 'tamil nadu'
  const accent = accentColor === 'emerald' ? 'text-emerald-400' : 'text-primary'
  const borderColor = accentColor === 'emerald' ? 'border-emerald-500/20' : 'border-primary/20'
  const bgColor = accentColor === 'emerald' ? 'bg-emerald-500/5' : 'bg-primary/5'

  const rows: { label: string; value: string; highlight?: boolean; negative?: boolean }[] = [
    { label: 'Base Price:', value: fmt(gst.originalPrice) },
    ...(discountPercent > 0
      ? [
          { label: `Discount (${discountPercent}%):`, value: `−${fmt(gst.discountAmount)}`, negative: true },
          { label: 'Price After Discount:', value: fmt(gst.priceAfterDiscount) },
        ]
      : []),
    ...(isTN
      ? [
          { label: 'CGST (9%):', value: fmt(gst.cgst) },
          { label: 'SGST (9%):', value: fmt(gst.sgst) },
        ]
      : [{ label: 'IGST (18%):', value: fmt(gst.igst) }]),
    {
      label: 'Total:',
      value: fmt(gst.finalInclTax),
      highlight: true,
    },
  ]

  return (
    <div className={`rounded-xl border ${borderColor} ${bgColor} overflow-hidden mt-3`}>
      <div className="divide-y divide-white/5">
        {rows.map((row, i) => (
          <div
            key={i}
            className={`flex justify-between items-center px-3 py-1.5 text-[11px] font-bold ${
              row.highlight
                ? `${accent} font-black bg-white/5 border-t border-white/10`
                : row.negative
                  ? 'text-emerald-400'
                  : 'text-text-secondary'
            }`}
          >
            <span className={row.highlight ? 'uppercase tracking-widest' : ''}>{row.label}</span>
            <span className={row.highlight ? `${accent} text-sm font-black` : ''}>{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
